import { PROD_RELEASE_SCHEDULE } from '../config/release-config'
import { getNextProdReleaseDate } from '../../utils/releaseSchedule'
import { Profile, Task, TaskUpdate } from '../types/database.types'

export type WorkflowState =
  | 'pending_quote'
  | 'assigned_to_dev'
  | 'in_dev'
  | 'released_to_dev'
  | 'in_dev_testing'
  | 'ready_for_uat'
  | 'in_uat_testing'
  | 'ready_for_release'
  | 'released_to_prod'
  | 'done'

export type WorkflowAction =
  | { type: 'ASSIGN_TO_DEV'; assigneeId: string }
  | { type: 'START_DEV' }
  | { type: 'RELEASE_TO_DEV' }
  | { type: 'TESTER1_PASSED' }
  | { type: 'TESTER1_FAILED' }
  | { type: 'RELEASE_TO_UAT' }
  | { type: 'TESTER2_PASSED' }
  | { type: 'TESTER2_FAILED' }
  | { type: 'RELEASE_TO_PROD' }

export type TransitionTaskContext = {
  users: Profile[]
  nextProdReleaseDate?: string
  random?: () => number
}

export type TransitionResult =
  | {
      ok: true
      nextState: WorkflowState
      updates: TaskUpdate
    }
  | {
      ok: false
      reason: string
    }

const ALLOWED_TRANSITIONS: Record<WorkflowState, WorkflowAction['type'][]> = {
  pending_quote: ['ASSIGN_TO_DEV'],
  assigned_to_dev: ['START_DEV'],
  in_dev: ['RELEASE_TO_DEV'],
  released_to_dev: [],
  in_dev_testing: ['TESTER1_PASSED', 'TESTER1_FAILED'],
  ready_for_uat: ['RELEASE_TO_UAT'],
  in_uat_testing: ['TESTER2_PASSED', 'TESTER2_FAILED'],
  ready_for_release: ['RELEASE_TO_PROD'],
  released_to_prod: [],
  done: [],
}

const getDefaultNextProdReleaseDate = () => {
  const nextRelease = getNextProdReleaseDate(PROD_RELEASE_SCHEDULE)
  return nextRelease.toISOString().slice(0, 10)
}

const isActionAllowed = (state: WorkflowState, action: WorkflowAction['type']) =>
  ALLOWED_TRANSITIONS[state].includes(action)

const getWorkflowState = (task: Task): WorkflowState => {
  switch (task.status) {
    case 'pending_quote':
      return task.assigned_to ? 'assigned_to_dev' : 'pending_quote'
    case 'in_dev_progress':
      return 'in_dev'
    case 'released_to_dev':
      return 'released_to_dev'
    case 'in_dev_testing':
      return 'in_dev_testing'
    case 'ready_for_uat':
      return 'ready_for_uat'
    case 'released_to_uat':
      return 'in_uat_testing'
    case 'in_uat_testing':
      return 'in_uat_testing'
    case 'ready_for_prod':
      return 'ready_for_release'
    case 'released_to_prod':
      return 'released_to_prod'
    case 'completed':
      return 'done'
    default:
      return 'pending_quote'
  }
}

const ensureActor = (currentUser: Profile | null): currentUser is Profile => {
  return Boolean(currentUser?.id)
}

const randomPick = (
  candidates: Profile[],
  excludeIds: string[],
  random: () => number
) => {
  const excluded = new Set(excludeIds.filter(Boolean))
  const eligible = candidates.filter((user) => !excluded.has(user.id))

  if (eligible.length === 0) {
    return null
  }

  const index = Math.floor(random() * eligible.length)
  return eligible[index]
}

export const transitionTask = (
  currentTask: Task,
  action: WorkflowAction,
  currentUser: Profile | null,
  context: TransitionTaskContext
): TransitionResult => {
  const currentState = getWorkflowState(currentTask)

  if (!isActionAllowed(currentState, action.type)) {
    return {
      ok: false,
      reason: `Invalid transition: ${currentState} cannot perform ${action.type}`,
    }
  }

  if (!ensureActor(currentUser)) {
    return {
      ok: false,
      reason: 'No authenticated user available for transition.',
    }
  }

  const isTestingResult = action.type.startsWith('TESTER1_') || action.type.startsWith('TESTER2_')
  if (currentUser.role === 'sales' && !isTestingResult) {
    return {
      ok: false,
      reason: 'Sales users may only submit first- or second-test results.',
    }
  }

  const random = context.random || Math.random
  const originalDevId = currentTask.original_dev_id
  const devTestAttempts = currentTask.dev_test_attempts || 0
  const secondTestAttempts = currentTask.second_test_attempts || 0

  switch (action.type) {
    case 'ASSIGN_TO_DEV': {
      return {
        ok: true,
        nextState: 'assigned_to_dev',
        updates: {
          status: 'pending_quote',
          assigned_to: action.assigneeId,
        },
      }
    }

    case 'START_DEV': {
      return {
        ok: true,
        nextState: 'in_dev',
        updates: {
          status: 'in_dev_progress',
        },
      }
    }

    case 'RELEASE_TO_DEV': {
      if (!currentTask.assigned_to) {
        return {
          ok: false,
          reason: 'Task must be assigned to a developer before release to dev testing.',
        }
      }

      const tester1 = randomPick(
        context.users.filter((user) => user.is_tester),
        [currentTask.assigned_to],
        random
      )

      if (!tester1) {
        return {
          ok: false,
          reason: 'No eligible first tester found.',
        }
      }

      return {
        ok: true,
        nextState: 'in_dev_testing',
        updates: {
          status: 'in_dev_testing',
          original_dev_id: currentTask.assigned_to,
          tester1_id: tester1.id,
          assigned_to: tester1.id,
          first_test: null,
          second_test: null,
        },
      }
    }

    case 'TESTER1_FAILED': {
      if (!originalDevId) {
        return {
          ok: false,
          reason: 'Task is missing original developer assignment. Reassign to the original developer and retry.',
        }
      }

      if (!currentUser.is_tester) {
        return {
          ok: false,
          reason: 'Current user is not allowed to submit first-test results.',
        }
      }

      return {
        ok: true,
        nextState: 'in_dev_testing',
        updates: {
          status: 'in_dev_testing',
          assigned_to: originalDevId,
          first_test: 'failed',
          dev_test_attempts: devTestAttempts + 1,
        },
      }
    }

    case 'TESTER1_PASSED': {
      if (!originalDevId) {
        return {
          ok: false,
          reason: 'originalDevId is required before second testing.',
        }
      }

      if (!currentUser.is_tester) {
        return {
          ok: false,
          reason: 'Current user is not allowed to submit first-test results.',
        }
      }

      const tester2 = randomPick(
        context.users.filter((user) => user.is_2nd_tester),
        [currentTask.tester1_id || '', originalDevId],
        random
      )

      if (!tester2) {
        return {
          ok: false,
          reason: 'No eligible second tester found.',
        }
      }

      return {
        ok: true,
        nextState: 'ready_for_uat',
        updates: {
          status: 'ready_for_uat',
          assigned_to: originalDevId,
          first_test: 'passed',
        },
      }
    }

    case 'RELEASE_TO_UAT': {
      if (!originalDevId) {
        return {
          ok: false,
          reason: 'originalDevId is required before second testing.',
        }
      }

      const tester2 = randomPick(
        context.users.filter((user) => user.is_2nd_tester),
        [currentTask.tester1_id || '', originalDevId],
        random
      )

      if (!tester2) {
        return {
          ok: false,
          reason: 'No eligible second tester found.',
        }
      }

      return {
        ok: true,
        nextState: 'in_uat_testing',
        updates: {
          status: 'in_uat_testing',
          assigned_to: tester2.id,
          tester2_id: tester2.id,
        },
      }
    }

    case 'TESTER2_FAILED': {
      if (!originalDevId) {
        return {
          ok: false,
          reason: 'Task is missing original developer assignment. Reassign to the original developer and retry.',
        }
      }

      if (!currentUser.is_2nd_tester) {
        return {
          ok: false,
          reason: 'Current user is not allowed to submit second-test results.',
        }
      }

      return {
        ok: true,
        nextState: 'in_dev_testing',
        updates: {
          status: 'in_dev_testing',
          assigned_to: originalDevId,
          second_test: 'failed',
          second_test_attempts: secondTestAttempts + 1,
        },
      }
    }

    case 'TESTER2_PASSED': {
      if (!currentUser.is_2nd_tester) {
        return {
          ok: false,
          reason: 'Current user is not allowed to submit second-test results.',
        }
      }

      return {
        ok: true,
        nextState: 'ready_for_release',
        updates: {
          status: 'ready_for_prod',
          overall_due_date: context.nextProdReleaseDate || getDefaultNextProdReleaseDate(),
          second_test: 'passed',
        },
      }
    }

    case 'RELEASE_TO_PROD': {
      return {
        ok: true,
        nextState: 'done',
        updates: {
          status: 'completed',
          overall_due_date: null,
          projected_completion: null,
        },
      }
    }

    default:
      return {
        ok: false,
        reason: 'Unsupported workflow action.',
      }
  }
}

export const getWorkflowActionForStatusChange = (
  task: Task,
  nextStatus: string,
  nextAssignedTo: string | null
): WorkflowAction | null => {
  const currentState = getWorkflowState(task)

  if (currentState === 'pending_quote' && nextStatus === 'pending_quote' && nextAssignedTo) {
    return { type: 'ASSIGN_TO_DEV', assigneeId: nextAssignedTo }
  }

  if (currentState === 'assigned_to_dev' && nextStatus === 'in_dev_progress') {
    return { type: 'START_DEV' }
  }

  if (currentState === 'in_dev' && nextStatus === 'released_to_dev') {
    return { type: 'RELEASE_TO_DEV' }
  }

  if (currentState === 'in_dev_testing' && nextStatus === 'ready_for_uat') {
    return { type: 'TESTER1_PASSED' }
  }

  if (currentState === 'ready_for_uat' && nextStatus === 'released_to_uat') {
    return { type: 'RELEASE_TO_UAT' }
  }

  if (currentState === 'in_uat_testing' && nextStatus === 'ready_for_prod') {
    return { type: 'TESTER2_PASSED' }
  }

  if (currentState === 'ready_for_release' && (nextStatus === 'released_to_prod' || nextStatus === 'completed')) {
    return { type: 'RELEASE_TO_PROD' }
  }

  return null
}

export const workflowGuards = {
  getWorkflowState,
  isActionAllowed,
  randomPick,
}